import { formatNameFirstLast, sortAsc } from '@strata/helper-functions';
import { FormContainer } from '@strata/react-form-container';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { PasswordEntry } from 'components/PasswordEntry';
import { DeleteUserDialog } from 'pages/userManagement/dialogs/DeleteUserDialog';
import { CREATE_ROUTE, UserType } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { filterSupplierRoles, handleDuplicateUserToast } from 'services/UserSupport';
import { getSupplierUserSchema } from 'services/Validators';

export const SupplierUserDetails = () => {
    const { upn } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(`/user-management/${UserType.SUPPLIER}`);

    const isCreate = upn === CREATE_ROUTE;

    const user = useGet(!isCreate ? `/authorization/supplier_users/${upn}` : null);
    const roles = useGet('/authorization/roles/');
    const suppliers = useGet('/reference/suppliers/');

    const parentSuppliers = useMemo(
        () =>
            suppliers
                ?.filter((s) => s.parent_supplier_npi == null)
                .map((supplier) => ({
                    supplier_npi: supplier.supplier_npi,
                    name: supplier.organization_name,
                    secondaryData: supplier.supplier_npi,
                }))
                .sort((a, b) => sortAsc(a.name, b.name)),
        [suppliers],
    );

    const roleOptions = filterSupplierRoles(roles);

    return (
        <Formik
            initialValues={{
                upn: user?.upn,
                full_name: user?.full_name,
                sms_number: user?.sms_number,
                supplier: parentSuppliers?.find(
                    (supplier) => supplier.supplier_npi === user?.pharmacies?.[0].supplier_npi,
                ),
                role: roleOptions?.find(
                    (role) => role.role_name === user?.pharmacies?.[0].role_name,
                ),
                analytics_access: user?.analytics_access ?? false,
            }}
            enableReinitialize
            validationSchema={getSupplierUserSchema(isCreate)}
            onSubmit={async (values) => {
                try {
                    let createResult;
                    if (isCreate) {
                        createResult = await post('/authorization/supplier_users/', {
                            upn: values.upn,
                            given_name: values.given_name,
                            family_name: values.family_name,
                            sms_number: values.sms_number,
                            name: formatNameFirstLast(values.given_name, values.family_name),
                            password: values.password,
                            analytics_access: values.analytics_access,
                            pharmacies: [
                                {
                                    supplier_npi: values.supplier.supplier_npi,
                                    role_name: values.role.role_name,
                                },
                            ],
                        });
                    } else {
                        await put(`/authorization/supplier_users/${values.upn}`, {
                            full_name: values.full_name,
                            sms_number: values.sms_number,
                            supplier_npi: values.supplier.supplier_npi,
                            role_name: values.role.role_name,
                            analytics_access: values.analytics_access,
                        });
                    }
                    if (createResult?.authzero_user_created === false) {
                        handleDuplicateUserToast(values.upn, UserType.SUPPLIER);
                    } else {
                        SuccessToast({
                            message: `Supplier User: ${values.upn} ${
                                isCreate ? 'created' : 'updated'
                            } successfully.`,
                        });
                    }
                    goHome();
                } catch (err) {
                    handleError('Error saving user.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle={`${isCreate ? 'Create' : 'Edit'} Supplier User`}
                    isLoading={!isCreate && !user}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formHeaderRightContent={
                        !isCreate && (
                            <DeleteUserDialog
                                userType={UserType.SUPPLIER}
                                onDeleteSuccess={goHome}
                            />
                        )
                    }
                    formLeftContent={
                        <>
                            <TextInputFormik
                                label="User Email"
                                id="upn"
                                disabled={!isCreate}
                                required
                            />
                            {isCreate ? (
                                <>
                                    <TextInputFormik label="First Name" id="given_name" required />
                                    <TextInputFormik label="Last Name" id="family_name" required />
                                    <PasswordEntry userType={UserType.SUPPLIER} />
                                </>
                            ) : (
                                <TextInputFormik label="User Name" id="full_name" required />
                            )}
                            <PhoneInputFormik label="SMS Number" id="sms_number" />
                            <SuggestSingleSelectFormik
                                id="supplier"
                                label="Parent Supplier"
                                required
                                isLoading={!parentSuppliers}
                                values={parentSuppliers}
                                helperText="The user's parent supplier"
                            />
                            <SuggestSingleSelectFormik
                                id="role"
                                label="Role"
                                required
                                isLoading={!roles}
                                values={roleOptions}
                                helperText={formik.values.role?.description}
                            />
                            <RadioButtonGroupFormik
                                name="analytics_access"
                                label="Analytics Access?"
                                options={['Yes', 'No']}
                                selectedValue={formik.values.analytics_access ? 'Yes' : 'No'}
                                onChange={(newValue) =>
                                    formik.setFieldValue('analytics_access', newValue === 'Yes')
                                }
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
